import "./style.scss";

import { Auth } from "aws-amplify";
import MultiStep from "components/shared/MultiStep";
import ImportFile, { quarterList } from "components/shared/MultiStep/ImportFile";
import LastStep from "components/shared/MultiStep/LastStep";
import ValidateFile from "components/shared/MultiStep/ValidateFile";
import { downloadFile } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { IDeclaration } from "store/contributionStore";
import type { ICompany } from "store/settingsStore";

interface URSSAFModalProps {
    contribution?: IDeclaration;
    quarter: number;
    year: number;
    onDeclared: (contribution?: IDeclaration) => void;
    onClose: () => void;
    activeStep?: number;
}

export const distributorTypes = ["DOE", "THC", "DCA"];

export type LastStepStatus = "infos" | "loading" | "corrections" | "once-only" | "success" | "PaymentDateOutOfRange";
export type ProfileType = "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "L" | "A" | "B" | "C" | "D" | "H" | "I";
export type Quarter = 1 | 2 | 3 | 4;

export interface BeneficiaryLine {
    profileType: ProfileType;
    socialSecurityNumber: string;
    lastname: string;
    firstname: string;
    streetNumber: string;
    streetNumberMultiplierAdverb: string;
    street: string;
    postalCode: string;
    city: string;
    country: string;
    birthDate?: string | Date;
    profitType: string;
    profitNature: string;
    paymentDate: string | Date;
    siretNumber: string;
    grossDuty: number;
    dcaRevenue: number;
    dcaCommission: number;
    quarter?: Quarter;
}

export const URSSAFModal = (props: URSSAFModalProps) => {
    const { contribution, quarter, year, onDeclared, activeStep = 0 } = props;

    const [t] = useTranslation();
    const { companyInfos } = useCompanySettings();
    const distributorType = companyInfos?.distributorType;

    const [localeActiveStep, setLocaleActiveStep] = useState(activeStep);
    const [uploadedFiles, setUploadedFiles] = useState<File>();
    const [normFields, setNormFields] = useState();
    const [lines, setLines] = useState<BeneficiaryLine[]>([]);
    const [errorList, setErrorList] = useState([]);
    const [lastStepStatus, setLastStepStatus] = useState<LastStepStatus>("infos");
    const [response, setResponse] = useState<any>();

    if (!distributorType) return <></>;

    const handleStepChange = (_: any, idx: number) => {
        setLocaleActiveStep(idx);
        setLastStepStatus("infos");
    };

    const handleFileUploadStep = (data: File) => {
        setUploadedFiles(data);
        setLocaleActiveStep((currStep) => currStep + 1);
    };

    const handleStepTwoSubmit = (data: BeneficiaryLine[], normFields: any) => {
        setLines(data);
        setNormFields(normFields);
        setLocaleActiveStep((currStep) => currStep + 1);
        handleStepThreeSubmit("infos", data);
    };

    const handleStepThreeSubmit = async (status: typeof lastStepStatus, data: string | BeneficiaryLine[]) => {
        const session = await Auth.currentSession();
        /**
         *
         * @param {BasicCompanyInfo} _company Company infos
         * @param {BeneficiaryLine[]} _lines Lines to export
         * @returns {Promise} Automatically passes from one step to another
         */
        const performQuery = async (_company: ICompany, _lines: BeneficiaryLine[]) => {
            try {
                // API Call
                const req = await fetch(
                    `${import.meta.env.REACT_APP_API_ENDPOINT}/contributions/${contribution?.id ?? -1}/generate`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: session.getAccessToken().getJwtToken(),
                        },
                        body: JSON.stringify({
                            // company: _company,
                            filename: "test.csv",
                            data: _lines.map((line) => ({
                                ...line,
                                // Replace , with . for js parsing
                                grossDuty: Math.round(+line.grossDuty?.toString().replace(",", ".") * 100),
                                dcaRevenue: Math.round(+line.dcaRevenue?.toString().replace(",", ".") * 100),
                                dcaCommission: Math.round(+line.dcaCommission?.toString().replace(",", ".") * 100),
                                paymentDate: (line.paymentDate as string).split("/").reverse().join("-"),
                                birthDate: line?.birthDate
                                    ? (line.birthDate as string).split("/").reverse().join("-")
                                    : "",
                            })),
                            // email: _email,
                        }),
                    },
                );

                if (req.status != 200) {
                    const res = await req.json();
                    setErrorList(res.data);
                    if (res.message == "ServerError.PaymentDateOutOfRange") setLastStepStatus("PaymentDateOutOfRange");
                    else setLastStepStatus("corrections");
                    if (res.message.some((item: string) => item.includes("is invalid.")))
                        toast.error(`${t("ServerError.InvalidDataFormat")}`);
                }

                if (req.status === 200) {
                    setResponse(await req.blob());
                    setLastStepStatus("success");
                }
            } catch (error) {
                // toast.error(`${t("ServerError.InternalServerError")}`);
            }
        };

        if (status === "infos") {
            setLastStepStatus("loading");
            await performQuery(companyInfos, data as BeneficiaryLine[]);
        }

        if (status === "corrections") {
            setLines(data as BeneficiaryLine[]);
            setLastStepStatus("loading");
            await performQuery(companyInfos, data as BeneficiaryLine[]);
        }
    };

    const handleFileDownload = async () => {
        const fileName = `declaration_urssaf_${companyInfos?.name}${quarter > 0 ? `_T${quarter}` : ""}_${year}.dat`;
        downloadFile({ source: response, fileName });
    };

    return contribution ? (
        <MultiStep
            id="form"
            activeStep={localeActiveStep}
            onStepChange={handleStepChange}
            standaloneMoveBetweenSteps
            title={`${t(`Declare.ModalTitle${quarterList[quarter]}`)} ${year}`}
            onClose={props.onClose}
            style={{ maxWidth: "98vw" }}
            steps={[
                {
                    title: t("Declare.Payments"),
                    component: <ImportFile quarter={quarter} onFileUpload={handleFileUploadStep} />,
                },
                {
                    title: t("Declare.Validation"),
                    component: (
                        <ValidateFile
                            distributorType={distributorType}
                            uploadedFile={uploadedFiles}
                            normFields={normFields}
                            onSubmit={handleStepTwoSubmit}
                        />
                    ),
                },
                {
                    title: t("Declare.Declaration"),
                    component: (
                        <LastStep
                            onSubmit={handleStepThreeSubmit}
                            status={lastStepStatus}
                            contribution={contribution}
                            quarter={quarter as Quarter}
                            onDownload={handleFileDownload}
                            onReset={() => setLocaleActiveStep(0)}
                            onDeclared={() => onDeclared(contribution)}
                            extraData={{ errors: errorList, lines }}
                        />
                    ),
                },
            ]}
        />
    ) : (
        <></>
    );
};
